import { z } from 'zod'

/** GET /api/v1/auth/session — the single source of truth for identity + roles. */
export const sessionSchema = z.discriminatedUnion('authenticated', [
  z.object({
    authenticated: z.literal(true),
    /** Always-present stable identifier for the signed-in principal. */
    subject: z.string(),
    // Absent (not null) for tenant-admin and staff sessions — the service
    // only materializes these for sessions backed by a user record.
    userId: z.string().optional(),
    email: z.email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    tenantId: z.string().nullish(),
    tenantSlug: z.string().nullish(),
    roles: z.array(z.string()),
    accessTokenExpiresAt: z.iso.datetime({ offset: true }),
    /** Present and true while a platform admin is impersonating (§5.9). */
    impersonating: z.boolean().optional(),
    impersonatedBy: z.string().nullish(),
  }),
  z.object({
    authenticated: z.literal(false),
  }),
])

export type Session = z.infer<typeof sessionSchema>
export type AuthenticatedSession = Extract<Session, { authenticated: true }>

/** GET /api/v1/auth/sso/resolve?email=… */
export const ssoResolutionSchema = z.union([
  z.object({
    ssoEnabled: z.literal(true),
    tenantSlug: z.string().nullish(),
    idpAlias: z.string(),
    idpDisplayName: z.string(),
    loginUrl: z.url(),
  }),
  z.object({
    ssoEnabled: z.literal(false),
  }),
])

export type SsoResolution = z.infer<typeof ssoResolutionSchema>

/** POST /api/v1/auth/refresh */
export const refreshResultSchema = z.object({
  accessTokenExpiresAt: z.iso.datetime({ offset: true }),
})

export type RefreshResult = z.infer<typeof refreshResultSchema>
